import React, { useState, useEffect, useCallback } from 'react';
import { chatService } from '../../services/api';
import { FolderIcon, DocumentIcon, FolderPlusIcon, TrashIcon, ChevronRightIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

const SectionHeader = ({ title, section, isOpen, onToggle }) => (
  <div 
    className="flex items-center cursor-pointer hover:opacity-80 mb-6"
    onClick={() => onToggle(section)}
  >
    <h1 className="text-3xl font-bold">{title}</h1>
    <div className="ml-3 text-gray-400">
      {isOpen ? <ChevronDownIcon className="w-6 h-6" /> : <ChevronRightIcon className="w-6 h-6" />}
    </div>
  </div>
);

const FolderItem = ({ folder, currentPath, onNavigate, onDrop, onDelete }) => {
  const handleDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.classList.add('bg-gray-700');
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove('bg-gray-700');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove('bg-gray-700');
    const fileId = e.dataTransfer.getData('text/plain');
    onDrop(fileId, folder.path);
  };

  const handleClick = (e) => {
    // Prevent folder navigation when clicking delete button
    if (e.target.closest('button')) {
      e.stopPropagation();
      return;
    }
    onNavigate(folder.path);
  };

  return (
    <div
      className="flex items-center justify-between p-2 hover:bg-gray-700 rounded cursor-pointer transition-colors"
      onClick={handleClick}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className="flex items-center">
        <FolderIcon className="w-5 h-5 text-yellow-400 mr-2" />
        <span>{folder.name}</span>
      </div>
      <button
        onClick={() => onDelete(folder.path)}
        className="text-gray-400 hover:text-red-400"
        title="Delete folder"
      >
        <TrashIcon className="w-5 h-5" />
      </button>
    </div>
  );
};

const FileItem = ({ file, onDelete }) => {
  const handleDragStart = (e) => {
    e.dataTransfer.setData('text/plain', file.file_id);
  };

  return (
    <div
      className="flex items-center justify-between p-2 hover:bg-gray-700 rounded transition-colors"
      draggable
      onDragStart={handleDragStart}
    >
      <div className="flex items-center">
        <DocumentIcon className="w-5 h-5 text-blue-400 mr-2" />
        <span>{file.name}</span>
      </div>
      <button
        onClick={() => onDelete(file.file_id)}
        className="text-gray-400 hover:text-red-400"
      >
        <TrashIcon className="w-5 h-5" />
      </button>
    </div>
  );
};

const FileManager = () => {
  const [sections, setSections] = useState({
    upload: true,
    files: true
  });
  const [items, setItems] = useState([]);
  const [currentPath, setCurrentPath] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [showNewFolderInput, setShowNewFolderInput] = useState(false);

  const loadItems = useCallback(async () => {
    try {
      const response = await chatService.listFiles(currentPath);
      setItems(response.items || []);
    } catch (error) {
      console.error('Error loading items:', error);
    }
  }, [currentPath]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const toggleSection = (section) => {
    setSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    setUploadError(null);

    const files = Array.from(e.dataTransfer.files);
    await handleFiles(files);
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    await handleFiles(files);
    e.target.value = null; // Reset input
  };

  const handleFiles = async (files) => {
    for (const file of files) {
      try {
        setIsUploading(true);
        const formData = new FormData();
        formData.append('file', file);
        if (currentPath) {
          formData.append('folder_path', currentPath);
        }
        await chatService.uploadFile(formData);
        await loadItems();
      } catch (error) {
        setUploadError(`Failed to upload ${file.name}: ${error.message}`);
      } finally {
        setIsUploading(false);
      }
    }
  };

  const handleDelete = async (fileId) => {
    try {
      await chatService.deleteFile(fileId);
      await loadItems();
    } catch (error) {
      // TODO: Add UI notification for delete errors
    }
  };

  const handleDeleteFolder = async (folderPath) => {
    if (window.confirm('Are you sure you want to delete this folder and all its contents?')) {
      try {
        await chatService.deleteFolder(folderPath);
        await loadItems();
      } catch (error) {
        console.error('Error deleting folder:', error);
      }
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    
    try {
      await chatService.createFolder({
        name: newFolderName,
        path: currentPath
      });
      setNewFolderName('');
      setShowNewFolderInput(false);
      await loadItems();
    } catch (error) {
      console.error('Error creating folder:', error);
    }
  };

  const handleMoveFile = async (fileId, destinationPath) => {
    try {
      await chatService.moveFile(fileId, destinationPath);
      await loadItems();
    } catch (error) {
      // TODO: Add UI notification for move errors
    }
  };

  const handleNavigate = (path) => {
    setCurrentPath(path);
  };

  const handleNavigateUp = () => {
    if (!currentPath) return;
    const parentPath = currentPath.split('/').slice(0, -1).join('/');
    setCurrentPath(parentPath);
  };

  return (
    <div className="flex-1 flex flex-col h-full bg-gray-900 text-white overflow-y-auto">
      <div className="p-8">
        {/* File Upload Section */}
        <div className="mb-12">
          <SectionHeader 
            title="Datei Upload" 
            section="upload" 
            isOpen={sections.upload}
            onToggle={toggleSection}
          />
          {sections.upload && (
            <div className="bg-gray-800 rounded-lg p-6">
              <div 
                className={`border-2 border-dashed ${isDragging ? 'border-indigo-400 bg-gray-700' : 'border-gray-600'} rounded-lg p-8 text-center`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <div className="space-y-2">
                  <DocumentIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="text-sm text-gray-400">
                    <label htmlFor="file-upload" className="relative cursor-pointer rounded-md font-medium text-indigo-400 hover:text-indigo-300 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500">
                      <span>Upload files</span>
                      <input 
                        id="file-upload" 
                        name="file-upload" 
                        type="file" 
                        className="sr-only" 
                        multiple
                        onChange={handleFileSelect}
                      />
                    </label>
                    <p className="pl-1">or drag and drop</p>
                  </div>
                  <p className="text-xs text-gray-500">
                    Upload files to be processed by the assistant
                  </p>
                  {isUploading && (
                    <div className="mt-2">
                      <div className="animate-pulse text-indigo-400">Uploading...</div>
                    </div>
                  )}
                  {uploadError && (
                    <div className="mt-2 text-red-400 text-sm">
                      {uploadError}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* File List Section */}
        <div className="mb-12">
          <div className="flex items-center justify-between">
            <SectionHeader 
              title="Files & Folders" 
              section="files" 
              isOpen={sections.files}
              onToggle={toggleSection}
            />
            <button
              onClick={() => setShowNewFolderInput(true)}
              className="flex items-center px-3 py-2 bg-indigo-600 rounded-md hover:bg-indigo-700 transition-colors mb-6"
            >
              <FolderPlusIcon className="w-5 h-5 mr-2" />
              New Folder
            </button>
          </div>
          
          {sections.files && (
            <div className="bg-gray-800 rounded-lg p-6">
              {/* Path Navigation */}
              <div className="flex items-center mb-4 space-x-2">
                <button
                  onClick={handleNavigateUp}
                  disabled={!currentPath}
                  className={`px-2 py-1 rounded ${!currentPath ? 'text-gray-500' : 'hover:bg-gray-700'}`}
                >
                  ..
                </button>
                <span className="text-gray-400">{currentPath || '/'}</span>
              </div>

              {/* New Folder Input */}
              {showNewFolderInput && (
                <div className="flex items-center mb-4 space-x-2">
                  <input
                    type="text"
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    placeholder="Enter folder name"
                    className="flex-1 px-3 py-2 bg-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <button
                    onClick={handleCreateFolder}
                    className="px-3 py-2 bg-indigo-600 rounded-md hover:bg-indigo-700"
                  >
                    Create
                  </button>
                  <button
                    onClick={() => {
                      setShowNewFolderInput(false);
                      setNewFolderName('');
                    }}
                    className="px-3 py-2 bg-gray-700 rounded-md hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                </div>
              )}

              {/* Items List */}
              <div className="space-y-1">
                {items
                  .sort((a, b) => {
                    // Folders first, then files
                    if (a.type !== b.type) {
                      return a.type === 'folder' ? -1 : 1;
                    }
                    return a.name.localeCompare(b.name);
                  })
                  .map((item) => (
                    item.type === 'folder' ? (
                      <FolderItem
                        key={item.path}
                        folder={item}
                        currentPath={currentPath}
                        onNavigate={handleNavigate}
                        onDrop={handleMoveFile}
                        onDelete={handleDeleteFolder}
                      />
                    ) : (
                      <FileItem
                        key={item.file_id}
                        file={item}
                        onDelete={handleDelete}
                      />
                    )
                  ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FileManager;
